import { TaskFilterOptions, TaskType } from '@griply/types';
import { chunkArray } from '@griply/utils';

export type FilteredTaskQueryConfig = {
  ids?: string[];
  type?: string;
  uncompletedOnly?: boolean;
  showCompleted?: boolean;
  deadlineStartDate?: Date;
  deadlineEndDate?: Date;
  deadlineDeadlineStartDate?: Date;
  deadlineDeadlineEndDate?: Date;
  completedStartDate?: Date;
  completedEndDate?: Date;
  tasksOnly?: boolean;
};

export const buildFilteredTaskQueryConfigs = (
  filterOptions: TaskFilterOptions[],
): FilteredTaskQueryConfig[] => {
  const allConfigs: FilteredTaskQueryConfig[] = [];

  filterOptions.forEach((filterSet) => {
    // Skip if types filter excludes TaskType.Task
    if (filterSet.types?.length && !filterSet.types.includes(TaskType.Task)) {
      return;
    }

    // Build base config with query-expressible fields.
    //
    // noDeadline / hasDeadline / noDeadlineDeadline / hasDeadlineDeadline are
    // intentionally NOT pushed down to Firestore: `where('field', '==', null)`
    // fails to match documents where the field is missing entirely (Firestore
    // distinguishes "absent" from "null"), which silently drops legitimate
    // tasks created before those fields were added to the schema. They are
    // applied client-side by `filterTasksClientSide` instead.
    //
    // goals / lifeAreas / noGoal / noLifeArea are not pushed down either: the
    // query can only match the single-valued legacy scalars, so a task linked
    // to a second goal is never fetched for it. `filterTasksClientSide` reads
    // the `goalIds` / `lifeAreaIds` arrays and narrows there.
    const baseConfig: FilteredTaskQueryConfig = {
      showCompleted: filterSet.showCompleted,
      deadlineStartDate: filterSet.deadlineStartDate,
      deadlineEndDate: filterSet.deadlineEndDate,
      deadlineDeadlineStartDate: filterSet.deadlineDeadlineStartDate,
      deadlineDeadlineEndDate: filterSet.deadlineDeadlineEndDate,
      completedStartDate: filterSet.completedStartDate,
      completedEndDate: filterSet.completedEndDate,
      tasksOnly:
        !filterSet.types?.length || filterSet.types.includes(TaskType.Task),
    };

    const { ids } = filterSet;

    // Skip if ids is set but empty (no tasks match)
    if (ids && !ids.length) {
      return;
    }

    // Only `ids` still narrows the query, so a split is only needed past its 30-value cap.
    if ((ids?.length ?? 0) <= 30) {
      allConfigs.push({ ...baseConfig, ids });
      return;
    }

    // Split queries - only `ids` needs chunking now.
    const { tasksOnly, showCompleted, ...rest } = baseConfig;

    // Chunk ids
    if (ids?.length) {
      chunkArray(ids, 30).forEach((chunked) => {
        allConfigs.push({
          ...rest,
          tasksOnly,
          showCompleted,
          ids: chunked,
        });
      });
    }
  });

  return allConfigs;
};
